import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
    Zap,
    ArrowLeft,
    Loader2,
    Calculator as CalcIcon,
    Download,
    Star as StarIcon,
    Leaf,
    TrendingDown,
    Share2,
} from "lucide-react";
import {
    PieChart,
    Pie,
    Cell,
    Tooltip,
    ResponsiveContainer,
    Legend,
} from "recharts";
import CountUp from "react-countup";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";
import useSeo from "../../hooks/useSeo";
import { Switch } from "../../components/ui/switch";
import ShareDialog from "../../components/calc/ShareDialog";

const PIE_COLORS = ["#39c8c1", "#d6b06b", "#3b82f6", "#a855f7", "#22c55e", "#94a3b8"];

const BREAKDOWN_LABELS = {
    hvac: "HVAC (Isıtma+Soğutma)",
    hot_water: "Sıcak Su",
    lighting: "Aydınlatma",
    kitchen: "Mutfak",
    common_areas: "Ortak Alanlar",
    other: "Diğer",
};

const SAVINGS_LABELS = {
    led_lighting: "LED Aydınlatma",
    solar_panel: "GES (Güneş Paneli)",
    bms_automation: "BMS Otomasyon",
    heat_recovery: "Isı Geri Kazanım",
};

const initialForm = {
    star_target: 4,
    room_count: 100,
    occupancy_rate: 0.65,
    climate_zone: "marmara",
    heating_type: "natural_gas",
    extras: { pool: true, spa: true, restaurant: true, meeting_room: true },
};

export default function EnergyCalculator() {
    const [form, setForm] = useState(initialForm);
    const [defaults, setDefaults] = useState(null);
    const [result, setResult] = useState(null);
    const [loading, setLoading] = useState(false);
    const [shareModal, setShareModal] = useState(false);

    useSeo({
        title: "Enerji Hesaplayıcı",
        description: "Otel için yıllık enerji tüketimi ve faturası tahmini, kategori bazlı dağılım, CO₂ salınımı ve tasarruf potansiyeli.",
        path: "/araclar/enerji-hesaplayici",
    });

    useEffect(() => {
        api.get("/calculator/defaults").then(({ data }) => setDefaults(data)).catch(() => { });
    }, []);

    const update = (path, value) => {
        setForm((prev) => {
            const next = JSON.parse(JSON.stringify(prev));
            const segs = path.split(".");
            let o = next;
            for (let i = 0; i < segs.length - 1; i++) o = o[segs[i]];
            o[segs[segs.length - 1]] = value;
            return next;
        });
    };

    const calculate = async () => {
        setLoading(true);
        try {
            const { data } = await api.post("/calculator/energy", form);
            setResult(data);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setLoading(false);
        }
    };

    const downloadPdf = async () => {
        try {
            const res = await api.post("/calculator/energy/pdf", form, { responseType: "blob" });
            const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
            const a = document.createElement("a");
            a.href = url;
            a.download = "otelpro-enerji-raporu.pdf";
            a.click();
            window.URL.revokeObjectURL(url);
            toast.success("PDF indirildi");
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    const breakdownData = result
        ? Object.entries(result.breakdown).map(([k, v]) => ({ name: BREAKDOWN_LABELS[k] || k, value: v.kwh }))
        : [];

    return (
        <div className="bg-light-bg min-h-[80vh]" data-testid="energy-calculator-page">
            <section className="bg-dark-navy text-white relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-[#08111d] via-[#0c2438] to-[#123b52]" />
                <div className="relative max-w-5xl mx-auto px-6 py-12 lg:py-16">
                    <Link to="/araclar" className="text-xs uppercase tracking-[0.2em] text-teal font-semibold inline-flex items-center gap-1 hover:text-white">
                        <ArrowLeft className="w-3.5 h-3.5" /> Tüm araçlar
                    </Link>
                    <h1 className="mt-3 text-3xl sm:text-5xl font-bold tracking-tight inline-flex items-center gap-3">
                        <Zap className="w-8 h-8 text-gold" />
                        Enerji Hesaplayıcı
                    </h1>
                    <p className="mt-3 text-white/75 max-w-2xl">
                        Yıldız, oda sayısı, iklim bölgesi ve tesis donanımına göre yıllık enerji maliyeti, kategori bazlı tüketim ve tasarruf potansiyeli.
                    </p>
                </div>
            </section>

            <div className="max-w-5xl mx-auto px-6 py-10 grid lg:grid-cols-2 gap-6">
                <Card title="Tesis Bilgileri">
                    <div className="space-y-5">
                        <Field label="Hedef Yıldız" Icon={StarIcon}>
                            <div className="flex gap-2">
                                {[1, 2, 3, 4, 5].map((s) => (
                                    <button
                                        key={s}
                                        type="button"
                                        onClick={() => update("star_target", s)}
                                        data-testid={`energy-star-${s}`}
                                        className={`flex-1 py-3 rounded-md border text-sm font-semibold transition ${form.star_target === s ? "bg-gold text-dark-navy border-gold" : "bg-white text-deep-blue border-slate-300 hover:border-gold/40"}`}
                                    >
                                        {Array.from({ length: s }).map(() => "★").join("")}
                                    </button>
                                ))}
                            </div>
                        </Field>

                        <Field label={`Oda Sayısı: ${form.room_count}`}>
                            <div className="flex items-center gap-3">
                                <input
                                    type="range"
                                    min={20}
                                    max={500}
                                    step={5}
                                    value={form.room_count}
                                    onChange={(e) => update("room_count", parseInt(e.target.value, 10))}
                                    className="flex-1 accent-teal"
                                    data-testid="energy-rooms-slider"
                                />
                                <input
                                    type="number"
                                    min={1}
                                    value={form.room_count}
                                    onChange={(e) => update("room_count", parseInt(e.target.value, 10) || 1)}
                                    className="w-20 px-2 py-2 rounded-md border border-slate-300 text-sm font-data text-center"
                                    data-testid="energy-rooms-input"
                                />
                            </div>
                        </Field>

                        <Field label={`Doluluk Oranı: %${Math.round(form.occupancy_rate * 100)}`}>
                            <input
                                type="range"
                                min={20}
                                max={95}
                                value={Math.round(form.occupancy_rate * 100)}
                                onChange={(e) => update("occupancy_rate", parseInt(e.target.value) / 100)}
                                className="w-full accent-teal"
                                data-testid="energy-occupancy"
                            />
                        </Field>

                        <Field label="İklim Bölgesi">
                            <select
                                value={form.climate_zone}
                                onChange={(e) => update("climate_zone", e.target.value)}
                                className="w-full px-3 py-2.5 rounded-md border border-slate-300 bg-white text-sm outline-none focus:border-teal"
                                data-testid="energy-climate"
                            >
                                {(defaults?.climate_zones || []).map((c) => (
                                    <option key={c.value} value={c.value}>{c.label}</option>
                                ))}
                            </select>
                        </Field>

                        <Field label="Isıtma Sistemi">
                            <div className="grid grid-cols-3 gap-2">
                                {(defaults?.heating || []).map((h) => (
                                    <Chip
                                        key={h.value}
                                        active={form.heating_type === h.value}
                                        onClick={() => update("heating_type", h.value)}
                                        data-testid={`energy-heating-${h.value}`}
                                    >
                                        {h.label}
                                    </Chip>
                                ))}
                            </div>
                        </Field>

                        <Field label="Ek Alanlar">
                            <div className="grid sm:grid-cols-2 gap-3">
                                <SimpleToggle label="Yüzme Havuzu" active={form.extras.pool} onToggle={(v) => update("extras.pool", v)} testId="energy-pool" />
                                <SimpleToggle label="Spa / Wellness" active={form.extras.spa} onToggle={(v) => update("extras.spa", v)} testId="energy-spa" />
                                <SimpleToggle label="Restoran" active={form.extras.restaurant} onToggle={(v) => update("extras.restaurant", v)} testId="energy-restaurant" />
                                <SimpleToggle label="Toplantı Salonu" active={form.extras.meeting_room} onToggle={(v) => update("extras.meeting_room", v)} testId="energy-meeting" />
                            </div>
                        </Field>

                        <button
                            onClick={calculate}
                            disabled={loading}
                            className="w-full bg-gold hover:bg-gold-700 text-dark-navy font-bold text-sm py-3 rounded-md inline-flex items-center justify-center gap-2 disabled:opacity-70 shadow-md"
                            data-testid="energy-calculate-btn"
                        >
                            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <CalcIcon className="w-4 h-4" />}
                            Hesapla
                        </button>
                    </div>
                </Card>

                {result ? (
                    <Card title="Sonuç">
                        <div className="bg-dark-navy text-white rounded-lg p-5">
                            <span className="text-[10px] uppercase tracking-[0.2em] text-teal font-semibold">Yıllık Enerji Maliyeti</span>
                            <div className="mt-1 font-data text-3xl sm:text-4xl font-bold text-gold">
                                <CountUp end={result.annual_cost} duration={1.6} separator="." decimals={0} />
                                <span className="ml-2 text-xl text-white/80">₺</span>
                            </div>
                            <div className="mt-2 text-xs text-white/60">
                                Yıllık kWh: <strong className="text-white">{result.annual_kwh.toLocaleString("tr-TR")}</strong> · Aylık ortalama: <strong className="text-white">{result.monthly_cost.toLocaleString("tr-TR")} ₺</strong>
                            </div>
                        </div>

                        <div className="grid grid-cols-2 gap-3 mt-4">
                            <div className="rounded-md bg-light-bg p-3">
                                <div className="text-[10px] uppercase tracking-wider text-deep-blue/70 font-semibold inline-flex items-center gap-1.5">
                                    <Leaf className="w-3 h-3 text-data-green" /> CO₂ Salınımı
                                </div>
                                <div className="mt-1 font-data text-xl font-bold text-data-green">
                                    <CountUp end={result.co2_tons_year} decimals={1} duration={1.4} /> ton/yıl
                                </div>
                            </div>
                            <div className="rounded-md bg-light-bg p-3">
                                <div className="text-[10px] uppercase tracking-wider text-deep-blue/70 font-semibold">Oda Başı Tüketim</div>
                                <div className="mt-1 font-data text-xl font-bold text-dark-navy">
                                    <CountUp end={result.kwh_per_room} duration={1.4} /> kWh
                                </div>
                            </div>
                        </div>

                        <div className="mt-5">
                            <h4 className="text-xs font-semibold uppercase tracking-wider text-deep-blue mb-2">Tüketim Dağılımı</h4>
                            <div className="h-56" data-testid="energy-breakdown-chart">
                                <ResponsiveContainer width="100%" height="100%">
                                    <PieChart>
                                        <Pie data={breakdownData} dataKey="value" nameKey="name" innerRadius={45} outerRadius={80} paddingAngle={2}>
                                            {breakdownData.map((_, i) => (
                                                <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                                            ))}
                                        </Pie>
                                        <Tooltip
                                            contentStyle={{ borderRadius: 8, fontSize: 12 }}
                                            formatter={(v) => `${v.toLocaleString("tr-TR")} kWh`}
                                        />
                                        <Legend wrapperStyle={{ fontSize: 10 }} />
                                    </PieChart>
                                </ResponsiveContainer>
                            </div>
                        </div>

                        <button
                            onClick={downloadPdf}
                            className="mt-4 w-full bg-deep-blue hover:bg-dark-navy text-white text-sm font-semibold py-2.5 rounded-md inline-flex items-center justify-center gap-2"
                            data-testid="energy-pdf-btn"
                        >
                            <Download className="w-4 h-4" /> PDF Rapor İndir
                        </button>
                        <button
                            onClick={() => setShareModal(true)}
                            className="mt-2 w-full bg-teal hover:bg-teal-700 text-dark-navy text-sm font-bold py-2.5 rounded-md inline-flex items-center justify-center gap-2"
                            data-testid="energy-share-btn"
                        >
                            <Share2 className="w-4 h-4" /> Sosyal Medyada Paylaş
                        </button>
                    </Card>
                ) : (
                    <Card title="Sonuç">
                        <div className="text-center py-12 text-deep-blue/60 text-sm">
                            <Zap className="w-10 h-10 mx-auto mb-3 text-teal/50" />
                            Enerji maliyetinizi görmek için soldaki formu doldurun.
                        </div>
                    </Card>
                )}
            </div>

            {result && (
                <div className="max-w-5xl mx-auto px-6 pb-14">
                    <Card title="Tasarruf Potansiyeli (Yıllık)">
                        <div className="grid sm:grid-cols-2 gap-3" data-testid="energy-savings-grid">
                            {Object.entries(result.savings_potential).map(([k, v]) => (
                                <div key={k} className="rounded-md bg-light-bg p-4 border border-slate-100 flex items-start gap-3">
                                    <span className="inline-flex w-9 h-9 rounded-md items-center justify-center bg-data-green/10 text-data-green shrink-0">
                                        <TrendingDown className="w-4 h-4" />
                                    </span>
                                    <div className="flex-1">
                                        <div className="text-sm font-semibold text-dark-navy">{SAVINGS_LABELS[k] || k}</div>
                                        <div className="font-data text-lg font-bold text-data-green mt-1">{v.toLocaleString("tr-TR")} ₺</div>
                                    </div>
                                </div>
                            ))}
                            <div className="sm:col-span-2 rounded-md bg-data-green/10 border border-data-green/30 p-4 mt-2">
                                <div className="text-xs uppercase tracking-wider text-data-green font-semibold">Toplam Potansiyel Tasarruf / Yıl</div>
                                <div className="font-data text-2xl font-bold text-data-green mt-1">
                                    {result.total_potential_saving_year.toLocaleString("tr-TR")} ₺
                                </div>
                            </div>
                        </div>
                    </Card>
                </div>
            )}

            {shareModal && result && (
                <ShareDialog
                    open={shareModal}
                    onClose={() => setShareModal(false)}
                    tool="energy"
                    summary={{
                        annual_kwh: result.annual_kwh,
                        annual_cost: result.annual_cost,
                        co2_tons_year: result.co2_tons_year,
                        star: form.star_target,
                        room_count: form.room_count,
                    }}
                />
            )}
        </div>
    );
}

function Card({ title, children }) {
    return (
        <div className="bg-white rounded-lg border border-slate-100 shadow-md p-6">
            {title && <h2 className="text-sm font-semibold uppercase tracking-wider text-deep-blue mb-4">{title}</h2>}
            {children}
        </div>
    );
}

function Field({ label, Icon, children }) {
    return (
        <div>
            <span className="text-xs font-semibold text-deep-blue/80 uppercase tracking-wider inline-flex items-center gap-1.5">
                {Icon && <Icon className="w-3.5 h-3.5 text-teal" />}
                {label}
            </span>
            <div className="mt-2">{children}</div>
        </div>
    );
}

function Chip({ active, onClick, children, ...rest }) {
    return (
        <button
            type="button"
            onClick={onClick}
            className={`px-3 py-2.5 rounded-md border text-sm font-semibold transition ${active ? "bg-dark-navy text-white border-dark-navy" : "bg-white text-deep-blue border-slate-300 hover:border-teal/50"}`}
            {...rest}
        >
            {children}
        </button>
    );
}

function SimpleToggle({ label, active, onToggle, testId }) {
    return (
        <div className={`rounded-md border p-3 transition flex items-center justify-between ${active ? "border-teal bg-teal/5" : "border-slate-200 bg-white"}`}>
            <span className="text-sm font-semibold text-dark-navy">{label}</span>
            <Switch checked={!!active} onCheckedChange={onToggle} data-testid={`${testId}-toggle`} />
        </div>
    );
}
